import React, { FunctionComponent, useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import _get from 'lodash/get';

import IconRadio from '../../UI/IconRadio';
import { parsePrice, formatPrice } from '../../../utils/utils';
import { fetchNBOptions } from '../promiseAction';
import { showLoader } from '../../Loader/action';
import { updateTransaction } from '../promiseAction';
import { processTransaction } from '../promiseAction';
import { redirectToBankPage } from '../processTransactionResponse';
import { sendEvent2 } from '../../../utils/tracking';

const Form: FunctionComponent<{
  bankOptions: Array<object>;
  amount: number;
  updateInternetCharges: Function;
  type: string;
  paymentInfo: any;
  internetCharges: number;
  fromFastag: any;
}> = ({ bankOptions, amount, updateInternetCharges, type, paymentInfo, internetCharges, fromFastag }) => {
  const { register, handleSubmit, errors } = useForm();
  const dispatch = useDispatch();
  const fAmount = formatPrice(amount);

  const [error, toggleError] = useState(false);
  const [selected, setSelected] = useState('Select');
  const [allBanks, setAllBanks] = useState([]);
  const [apiError, setApiError] = useState('');

  const dispatchLoader = useCallback((payload: any) => dispatch(showLoader(payload)), [dispatch]);

  const dispatchProcessTransaction = useCallback(
    (payload: any) =>
      dispatch(processTransaction(payload)).then((resp: any) => {
        const resultInfo = _get(resp, 'data.body.resultInfo', {});
        if (resultInfo.resultStatus === 'S') {
          redirectToBankPage(payload.paymentMode, resp);
        } else {
          if (resultInfo && resultInfo.resultMsg) {
            setApiError(resultInfo.resultMsg);
          } else {
            setApiError('Something went wrong');
          }
        }
        dispatchLoader({ show: false });
      }),
    [dispatch, dispatchLoader],
  );

  const dispatchUpdateTransaction = useCallback(
    (payload: any) =>
      dispatch(updateTransaction(payload.params)).then((resp: any) => {
        if (resp.data.success) {
          const data: any = {
            requestType: 'NATIVE',
            mId: paymentInfo.merchantId,
            orderId: paymentInfo.transactionCode,
            token: paymentInfo.checkSumHash,
            ...payload.info,
          };
          dispatchProcessTransaction(data);
        } else {
          dispatchLoader({ show: false });
          setApiError(_get(resp, 'data.message', 'Something went wrong'));
        }
      }),
    [dispatch, paymentInfo, dispatchLoader, dispatchProcessTransaction],
  );

  const onSubmit = (values?: any) => {
    if (!(selected && selected !== 'Select')) {
      toggleError(true);
      return;
    }
    dispatchLoader({ show: true });
    const data: any = {
      params: {
        appVersion: 'web',
        paymentMode: 'NB',
        serviceProvider: 'PAYTM CUSTOM',
        userCode: paymentInfo.customerId,
        transactionCode: paymentInfo.transactionCode,
        transactionToken: paymentInfo.checkSumHash,
        amount: fAmount,
        pspAmount: parseFloat(formatPrice(internetCharges || 0)),
      },
      info: {
        channelCode: selected,
        paymentMode: 'NET_BANKING',
      },
    };
    if (fromFastag?.fromFastag === true) {
      sendEvent2(
        'pay_btn',
        {
          event_action: 'click',
          screen_name: 'wallet_recharge_screen',
          miscellaneous: JSON.stringify({
            type: 'net_banking',
          }),
          target_product: 'fastag',
          event_platform: 'web',
        },
        true,
      );
    }
    dispatchUpdateTransaction(data);
  };

  const dipatchFetchNB = useCallback(
    (payload: any) =>
      dispatch(fetchNBOptions(payload)).then((resp: any) =>
        setAllBanks(_get(resp, 'data.body.nbPayOption.payChannelOptions', [])),
      ),
    // TODO: handle dependencies history, userSelectedDemandId
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, paymentInfo],
  );

  useEffect(() => {
    dipatchFetchNB({
      token: paymentInfo.checkSumHash,
      mId: paymentInfo.merchantId,
      orderId: paymentInfo.transactionCode,
    });
  }, [paymentInfo, dipatchFetchNB]);

  const handleChange = (event: any) => {
    setSelected(event.target.value);
    toggleError(false);
    updateInternetCharges({ [type]: 'NB' });
    setApiError('');
  };

  return (
    <div className="p16 mode-container">
      <form className="form-container pr8" onSubmit={handleSubmit(onSubmit)}>
        {bankOptions && bankOptions.length ? (
          <div className="flex input-radio">
            {bankOptions.map((bank: any) => {
              return (
                <IconRadio
                  id="id"
                  value={bank.channelCode}
                  label={bank.channelCode}
                  selected={selected}
                  validation={register({ required: true })}
                  iconPath={bank.iconUrl}
                  handleChange={handleChange}
                  key={bank.channelCode}
                />
              );
            })}
          </div>
        ) : null}

        <div className="flex justify-content-s card-info mt16 pt16">
          {allBanks.length ? (
            <div className={`info-column flex-1 ${error ? 'error-field' : ''}`}>
              <p className="small mb8 font-light-grey form-label">All Banks</p>
              <select
                placeholder="Select"
                className="form-input"
                name="id"
                onChange={handleChange}
                ref={register({ required: true })}
                value={selected}
              >
                <option disabled value="Select">
                  Select
                </option>
                {allBanks.map((bank: any) => (
                  <option key={bank.channelCode} value={bank.channelCode}>
                    {bank.channelName}
                  </option>
                ))}
              </select>
              <p className="form-error absolute">{errors.id && errors.id.message}</p>
            </div>
          ) : null}
          <div className="info-column">
            <input
              className="form-btn"
              type="submit"
              value={`Pay ${parsePrice({ price: amount + internetCharges })}`}
            />
          </div>
        </div>
        <p className={`form-error absolute ${apiError ? '' : 'none'}`}>{apiError}</p>
      </form>
    </div>
  );
};

export default Form;
